import { motionOf } from '@flobi/artwork-core';
import type { RecipeLike } from './animateModel';

/**
 * A recipe card's picture: a little poster composed for what the recipe is FOR.
 *
 * The first cards were one layout — a photograph, a square, two lines — worn by every
 * recipe. The second gave each its own page but drew it in placeholder boxes, gradient
 * "photos" and a violet-and-peach palette, which read as generated. The third was drawn
 * as posters but flooded each in a colour of its own, and twenty posters in six colours
 * is a noise. So: ONE stock, black, and ONE red, on every card. What tells them apart is
 * what a designer would tell them apart by — the type and the composition: real words in
 * real faces, and the trade's own marks (a baseline, a halftone, a starburst, a ruled
 * card, circle-square-triangle), never a box standing in for something. At rest a card is
 * its finished poster; it plays when it is pointed at.
 *
 * Only the COMPOSITION is written here. What each piece does is still read from the
 * recipe itself — a piece names the role it plays, and takes that role's motion, feel,
 * direction, delay and text-by from the engine's own description — so a card cannot
 * promise a motion its recipe does not write.
 */

/** Whose motion a piece takes. `still` never moves: a baseline, the rules of a card. */
export type SceneRole = 'backdrop' | 'image' | 'text' | 'accent' | 'still';

/** The whole palette: black, the one red, and the stock showing through. */
export type Ink = 'ink' | 'red' | 'paper';

/** What a piece is drawn as. `text` is real words; the rest are the trade's own marks. */
export type SceneShape =
  | 'block'
  | 'disc'
  | 'half'
  | 'tri'
  | 'ring'
  | 'outline'
  | 'star'
  | 'spark'
  | 'asterisk'
  | 'bottle'
  | 'squiggle'
  | 'scribble'
  | 'wobble'
  | 'stripes'
  | 'lines'
  | 'dots'
  | 'dither'
  | 'steps'
  | 'torn'
  | 'text';

/** The faces: the chrome's own sans and mono, and three loaded when the recipes first show. */
export type SceneFace = 'sans' | 'mono' | 'serif' | 'display' | 'hand';

export interface ScenePiece {
  readonly role: SceneRole;
  readonly shape: SceneShape;
  readonly ink: Ink;
  /** Left, top, width and height, in hundredths of the card. No height: as tall as it is wide. */
  readonly box: readonly [number, number, number, number?];
  /** Degrees it sits turned or slanted at rest, before any motion. */
  readonly turn?: number;
  readonly skew?: number;
  /** A block with its corners eased, for a phone's frame. Everything else is cut square. */
  readonly round?: boolean;
  /** Printed solid. Otherwise the red overprints what is under it, as ink does. */
  readonly flat?: boolean;
  /** How strongly it is printed, 0 to 1. */
  readonly faint?: number;
  /** A starburst's points. */
  readonly points?: number;
  /** Words, for a `text` piece. A line break is a line break. */
  readonly text?: string;
  /** Type size, in hundredths of the card's width. */
  readonly size?: number;
  readonly weight?: number;
  readonly face?: SceneFace;
  readonly italic?: boolean;
  /** Letter-spacing and line-height, in ems. */
  readonly track?: number;
  readonly lead?: number;
  readonly align?: 'left' | 'center';
  /** An ink per word, in order; the last one carries on. None: the piece's own ink. */
  readonly tints?: readonly Ink[];
  /** A caret after the last letter, for text that is typed. */
  readonly caret?: Ink;
}

export type Scene = readonly ScenePiece[];

/** A Swiss page: what a recipe with no scene of its own is drawn as. */
const PAGE: Scene = [
  { role: 'image', shape: 'block', ink: 'ink', box: [58, 12, 34, 76] },
  { role: 'text', shape: 'text', ink: 'ink', box: [8, 13, 48, 36], text: 'Annual\nReport', size: 9.5, weight: 600, track: -0.02, lead: 1.05 },
  { role: 'accent', shape: 'block', ink: 'red', box: [8, 60, 40, 1.2] },
  { role: 'text', shape: 'text', ink: 'ink', box: [8, 67, 30, 12], text: '2026', size: 6, weight: 500, face: 'mono' },
];

export const SCENES: Readonly<Record<string, Scene>> = {
  // ── Words first ──────────────────────────────────────────────────────────────
  // Rams's line, set as an editorial headline: the last word lands in red.
  'headline-reveal': [
    { role: 'text', shape: 'text', ink: 'ink', box: [8, 22, 88, 60], text: 'Less,\nbut better', size: 17.5, weight: 400, face: 'serif', lead: 0.98, track: -0.01, tints: ['ink', 'ink', 'red'] },
    { role: 'accent', shape: 'block', ink: 'ink', box: [8, 13, 16, 2.4] },
    { role: 'accent', shape: 'block', ink: 'ink', box: [8, 88, 84, 1] },
  ],
  // A title card: each line comes up from behind its own rule.
  'masked-title': [
    { role: 'text', shape: 'text', ink: 'ink', box: [8, 14, 84, 30], text: 'OPENING', size: 18, weight: 400, face: 'display', lead: 1 },
    { role: 'still', shape: 'block', ink: 'ink', box: [8, 45.5, 84, 1] },
    { role: 'text', shape: 'text', ink: 'red', box: [8, 51, 84, 30], text: 'NIGHT', size: 18, weight: 400, face: 'display', lead: 1 },
    { role: 'still', shape: 'block', ink: 'ink', box: [8, 82.5, 84, 1] },
  ],
  // A type poster: condensed capitals, floor to ceiling.
  'kinetic-words': [
    { role: 'text', shape: 'text', ink: 'ink', box: [7, 7, 62, 90], text: 'TYPE\nTHAT\nMOVES', size: 19, weight: 400, face: 'display', lead: 0.96, tints: ['ink', 'red', 'ink'] },
    { role: 'accent', shape: 'disc', ink: 'ink', box: [70, 12, 20] },
  ],
  // A ruled index card, a red margin, and a postmark.
  'typewriter-title': [
    { role: 'still', shape: 'lines', ink: 'ink', box: [0, 22, 100, 78], faint: 0.16 },
    { role: 'still', shape: 'block', ink: 'red', box: [15, 0, 0.8, 100], faint: 0.8 },
    { role: 'text', shape: 'text', ink: 'ink', box: [20, 27, 78, 20], text: 'Dear reader,', size: 9.2, weight: 500, face: 'mono', caret: 'red' },
    { role: 'accent', shape: 'ring', ink: 'red', box: [73, 56, 17], turn: -12 },
  ],

  // ── Show a thing ─────────────────────────────────────────────────────────────
  // A pack shot, with the seal every pack shot gets.
  'product-pop': [
    { role: 'image', shape: 'bottle', ink: 'ink', box: [41, 9, 18, 60] },
    { role: 'accent', shape: 'star', ink: 'red', box: [63, 8, 24], points: 12 },
    { role: 'accent', shape: 'disc', ink: 'ink', box: [18, 50, 9] },
    { role: 'text', shape: 'text', ink: 'ink', box: [20, 75, 60, 18], text: 'NEW', size: 9, weight: 400, face: 'display', track: 0.12, align: 'center' },
  ],
  // The pack, and a price seal that falls onto the page beside it.
  'price-drop': [
    { role: 'image', shape: 'bottle', ink: 'ink', box: [16, 12, 17, 68] },
    { role: 'accent', shape: 'star', ink: 'red', box: [44, 10, 44], points: 16 },
    { role: 'text', shape: 'text', ink: 'paper', box: [44, 34, 44, 26], text: '−50%', size: 13, weight: 400, face: 'display', align: 'center' },
  ],
  // Two inks: a sun going into the sea.
  'soft-focus': [
    { role: 'image', shape: 'disc', ink: 'red', box: [52, 20, 31], flat: true },
    { role: 'backdrop', shape: 'block', ink: 'ink', box: [0, 60, 100, 40] },
    { role: 'accent', shape: 'lines', ink: 'paper', box: [10, 72, 34, 17] },
  ],
  // A picture in its frame, and a caption beside it.
  'slow-zoom': [
    { role: 'backdrop', shape: 'outline', ink: 'ink', box: [8, 10, 50, 80] },
    { role: 'image', shape: 'disc', ink: 'red', box: [20, 26, 26] },
    { role: 'image', shape: 'block', ink: 'ink', box: [8, 66, 50, 1] },
    { role: 'text', shape: 'text', ink: 'ink', box: [64, 38, 34, 24], text: 'closer', size: 11.5, weight: 400, face: 'serif', italic: true },
  ],

  // ── Brand moments ────────────────────────────────────────────────────────────
  // A mark over a spaced wordmark.
  'logo-sting': [
    { role: 'accent', shape: 'asterisk', ink: 'red', box: [42, 13, 16] },
    { role: 'text', shape: 'text', ink: 'ink', box: [2, 61, 96, 16], text: 'ATELIER', size: 7.2, weight: 600, track: 0.34, align: 'center' },
  ],
  // Three frames of a story, each a poster of its own.
  'story-slides': [
    { role: 'image', shape: 'block', ink: 'ink', box: [9, 9, 25, 82], round: true },
    { role: 'accent', shape: 'disc', ink: 'paper', box: [13.5, 20, 16] },
    { role: 'image', shape: 'block', ink: 'red', box: [37.5, 9, 25, 82], round: true, flat: true },
    { role: 'accent', shape: 'half', ink: 'paper', box: [42, 50, 16, 13] },
    { role: 'image', shape: 'block', ink: 'ink', box: [66, 9, 25, 82], round: true },
    { role: 'accent', shape: 'stripes', ink: 'paper', box: [70.5, 22, 16, 30] },
  ],

  // ── Loops ────────────────────────────────────────────────────────────────────
  // A sticker: a burst that beats, and sparkles that drift round it.
  'looping-gif': [
    { role: 'image', shape: 'star', ink: 'red', box: [31, 10, 38], points: 14 },
    { role: 'accent', shape: 'spark', ink: 'ink', box: [12, 16, 10] },
    { role: 'accent', shape: 'spark', ink: 'ink', box: [77, 14, 12] },
    { role: 'accent', shape: 'spark', ink: 'ink', box: [73, 60, 9] },
    { role: 'accent', shape: 'spark', ink: 'ink', box: [17, 64, 7] },
  ],
  // The Bauhaus three, dropped on a baseline.
  'bouncy-drop': [
    { role: 'still', shape: 'block', ink: 'ink', box: [8, 82, 84, 1.6] },
    { role: 'accent', shape: 'disc', ink: 'ink', box: [12, 53.2, 18] },
    { role: 'accent', shape: 'block', ink: 'red', box: [38, 50, 20] },
    { role: 'image', shape: 'tri', ink: 'ink', box: [66, 46.8, 22] },
  ],
  // A shop sign: the word flickers, the tube round it glows.
  'neon-sign': [
    { role: 'accent', shape: 'outline', ink: 'red', box: [14, 16, 72, 68], round: true },
    { role: 'text', shape: 'text', ink: 'red', box: [14, 30, 72, 40], text: 'OPEN', size: 21, weight: 400, face: 'display', track: 0.06, align: 'center' },
  ],
  // Stickers, slapped on over each other.
  'sticker-pile': [
    { role: 'image', shape: 'star', ink: 'red', box: [9, 12, 36], points: 10, turn: -10 },
    { role: 'accent', shape: 'disc', ink: 'ink', box: [35, 34, 28] },
    { role: 'image', shape: 'tri', ink: 'red', box: [58, 10, 27], turn: 14 },
    { role: 'accent', shape: 'spark', ink: 'ink', box: [70, 54, 18] },
    { role: 'accent', shape: 'spark', ink: 'paper', box: [43, 47, 12], turn: 20 },
  ],

  // ── Moods: ten tempers, each in the voice a designer would set it in ──────────
  professional: PAGE,
  bold: [
    { role: 'text', shape: 'text', ink: 'ink', box: [6, 8, 70, 92], text: 'GO', size: 47, weight: 400, face: 'display', lead: 0.9 },
    { role: 'accent', shape: 'disc', ink: 'red', box: [69, 56, 18] },
  ],
  elegant: [
    { role: 'accent', shape: 'outline', ink: 'ink', box: [6, 9, 88, 82] },
    { role: 'image', shape: 'block', ink: 'red', box: [47.5, 19, 5], turn: 45 },
    { role: 'text', shape: 'text', ink: 'ink', box: [8, 35, 84, 38], text: 'Soirée', size: 19, weight: 400, face: 'serif', italic: true, align: 'center' },
    { role: 'accent', shape: 'block', ink: 'ink', box: [42, 79, 16, 1] },
  ],
  // A letterbox, a low sun, and a title in the corner.
  cinematic: [
    { role: 'still', shape: 'block', ink: 'ink', box: [0, 0, 100, 15] },
    { role: 'still', shape: 'block', ink: 'ink', box: [0, 85, 100, 15] },
    { role: 'image', shape: 'disc', ink: 'red', box: [60, 30, 24] },
    { role: 'text', shape: 'text', ink: 'ink', box: [9, 40, 50, 22], text: 'Scene one', size: 10.5, weight: 400, face: 'serif', italic: true },
  ],
  energetic: [
    { role: 'image', shape: 'block', ink: 'ink', box: [8, 13, 44, 3.6], skew: -24 },
    { role: 'image', shape: 'block', ink: 'ink', box: [22, 22, 40, 3.6], skew: -24 },
    { role: 'image', shape: 'block', ink: 'ink', box: [14, 31, 26, 3.6], skew: -24 },
    { role: 'text', shape: 'text', ink: 'ink', box: [8, 43, 66, 50], text: 'RUSH', size: 25, weight: 400, face: 'display', skew: -11 },
    { role: 'accent', shape: 'disc', ink: 'red', box: [74, 50, 17] },
  ],
  playful: [
    { role: 'image', shape: 'disc', ink: 'red', box: [8, 12, 30] },
    { role: 'accent', shape: 'squiggle', ink: 'ink', box: [46, 14, 44, 18] },
    { role: 'accent', shape: 'spark', ink: 'ink', box: [80, 56, 11] },
    { role: 'text', shape: 'text', ink: 'ink', box: [40, 36, 40, 56], text: 'yo!', size: 27, weight: 700, face: 'hand', turn: -6 },
  ],
  handmade: [
    { role: 'image', shape: 'wobble', ink: 'red', box: [8, 13, 35] },
    { role: 'text', shape: 'text', ink: 'ink', box: [48, 17, 48, 48], text: 'made by\nhand', size: 14.5, weight: 700, face: 'hand', lead: 0.88, turn: -3 },
    { role: 'accent', shape: 'scribble', ink: 'ink', box: [48, 66, 40, 14] },
  ],
  digital: [
    { role: 'text', shape: 'text', ink: 'ink', box: [8, 13, 70, 18], text: '> run', size: 9.5, weight: 700, face: 'mono', caret: 'red' },
    { role: 'accent', shape: 'block', ink: 'ink', box: [8, 36, 84, 1], faint: 0.5 },
    { role: 'image', shape: 'dither', ink: 'ink', box: [56, 45, 36, 43] },
    { role: 'accent', shape: 'steps', ink: 'red', box: [8, 72, 40, 10] },
  ],
  grunge: [
    { role: 'image', shape: 'torn', ink: 'ink', box: [8, 12, 54, 70], turn: -6 },
    { role: 'image', shape: 'dots', ink: 'ink', box: [60, 9, 32, 40] },
    { role: 'accent', shape: 'block', ink: 'red', box: [50, 56, 42, 13], turn: 8 },
    { role: 'text', shape: 'text', ink: 'paper', box: [10, 27, 50, 44], text: 'RAW', size: 22, weight: 400, face: 'display', turn: -6, align: 'center' },
  ],
  minimal: [
    { role: 'accent', shape: 'disc', ink: 'red', box: [47.5, 34, 5] },
    { role: 'text', shape: 'block', ink: 'ink', box: [38, 58, 24, 1] },
  ],
};

export function sceneFor(id: string): Scene {
  return SCENES[id] ?? PAGE;
}

/** The faces a card's words are set in, beyond the chrome's own: loaded once, when the recipes first show. */
export const SCENE_FACES_URL = 'https://fonts.googleapis.com/css2?family=Anton&family=Caveat:wght@700&family=Instrument+Serif:ital@0;1&display=swap';

/** What one piece does when its card plays. Seconds are the card's, not the page's. */
export interface SceneStep {
  /** The arrival, or 'none' for a piece that is simply there. */
  readonly enter: string;
  readonly direction: string;
  readonly ease: string;
  /** How much of the motion, for the card's little distances. */
  readonly amount: number;
  readonly delay: number;
  readonly span: number;
  /** What it keeps doing once it has arrived, or null. */
  readonly loop: string | null;
  /** How a text piece arrives, and the seconds between its words or letters. */
  readonly by: 'whole' | 'word' | 'letter';
  readonly gap: number;
}

/** The most a card's arrivals may take, in seconds: slower recipes are slowed to this, in proportion. */
export const MOST_PLAYING = 2.6;
/** How long a card rests on its finished page before it plays again. */
export const HOLD = 1.3;

/** The words or letters a text arrives in, as the card draws them. */
export function sceneUnits(text: string, by: SceneStep['by']): number {
  if (by === 'word') return text.split(/\s+/).filter(Boolean).length;
  if (by === 'letter') return [...text.replace(/\s+/g, '')].length;
  return 1;
}

/**
 * Each piece's part in the playing, read from the recipe, and how long one playing takes.
 *
 * The engine's own rules for a page (`planMagic`), in little: a piece takes its role's
 * part where the recipe gives one and the page's motion where it does not, at its turn in
 * the stagger plus the part's own delay. Under a words-first recipe the texts go first,
 * one after another in the order written here, and everything else waits for the first of
 * them to be in.
 */
export function sceneSteps(recipe: RecipeLike, scene: Scene): { steps: SceneStep[]; total: number } {
  const page = recipe.page;
  const stagger = page.order === 'together' ? 0 : (page.stagger ?? 0.08);
  const wordsFirst = recipe.lead === 'words';
  let turn = 0;
  let rank = 0;
  /** When the next text sets off, and when the first one is in. */
  let nextText: number | null = null;
  let wordsIn = 0;
  /** Pieces waiting on the words: their delays are added once every text is timed. */
  const waits: number[] = [];

  const raw = scene.map((piece, i): SceneStep => {
    if (piece.role === 'still') return { enter: 'none', direction: 'up', ease: 'smooth', amount: 1, delay: 0, span: 0, loop: null, by: 'whole', gap: 0 };
    const part = recipe.parts?.[piece.role] ?? null;
    const own = part ?? page;
    const place = turn++;
    const loops = !!own.motion && !!motionOf(own.motion)?.loops;
    const by = piece.text && (own.textBy === 'word' || own.textBy === 'letter') ? own.textBy : 'whole';
    const gap = by === 'whole' ? 0 : (own.textStagger ?? 0.04);
    const span = Math.max(0.2, own.span ?? 0.6);
    let delay = (part?.delay ?? 0) + place * stagger;
    if (wordsFirst && piece.role === 'text' && part) {
      const arrival = (sceneUnits(piece.text ?? '', by) - 1) * gap;
      delay = nextText ?? part.delay ?? 0;
      if (nextText === null) wordsIn = delay + arrival + span / 2;
      nextText = delay + arrival + Math.max(stagger, 0.1);
    } else if (wordsFirst) {
      delay = (part?.delay ?? 0) + rank++ * stagger;
      waits.push(i);
    }
    return {
      enter: loops ? 'none' : (own.motion ?? 'none'),
      direction: own.direction ?? page.direction ?? 'up',
      ease: own.ease ?? page.ease ?? 'out',
      amount: Math.max(0.5, Math.min(1.6, own.intensity ?? 1)),
      delay,
      span,
      loop: loops ? own.motion! : own.loop?.motion && own.loop.motion !== 'none' ? own.loop.motion : null,
      by,
      gap,
    };
  });
  for (const i of waits) raw[i] = { ...raw[i]!, delay: raw[i]!.delay + wordsIn };

  const ends = raw.map((step, i) => (step.enter === 'none' ? 0 : step.delay + step.span + (sceneUnits(scene[i]!.text ?? '', step.by) - 1) * step.gap));
  const longest = Math.max(0, ...ends);
  const k = longest > MOST_PLAYING ? MOST_PLAYING / longest : 1;
  const steps = raw.map((step) => ({ ...step, delay: step.delay * k, span: step.span * k, gap: step.gap * k }));
  return { steps, total: longest * k };
}
